import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { useDrawerLayoutContext } from "../../-DrawerLayoutContext";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useEffect } from "react";
import { PageHeader } from "@dmca/frontend/components/nav";
import {
  checkPermissionsInLoader,
  useSubscription,
} from "@dmca/frontend/hooks";
import { getCellStatus } from "./-cellStatus";

export const Route = createFileRoute("/_authed/_drawerLayout/admin/cell")({
  component: RouteComponent,
  loader: async ({ context }) => {
    await checkPermissionsInLoader("Admin::UpdateCell", context);
    const { trpcQcProxy: trpc, qc } = context;
    qc.prefetchQuery(trpc.cell.getAll.queryOptions());
    qc.prefetchQuery(trpc.iot.getAllWithStatus.queryOptions());
  },
  head: () => ({
    meta: [
      {
        title: "Manage Cells | DNA Dashboard",
      },
    ],
  }),
});

function RouteComponent() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const id = useMatches({
    select: (matches) => {
      const match = matches[matches.length - 1];
      if (match.id.includes("add-new")) {
        return "add-new";
      }
      return (match.params as any)?.id as string;
    },
  });
  const { data: cells, isLoading } = useQuery(trpc.cell.getAll.queryOptions());
  const { data: devices } = useQuery(trpc.iot.getAllWithStatus.queryOptions());
  useSubscription("onIOTDeviceStatusUpdate", () => {
    qc.invalidateQueries(trpc.iot.pathFilter());
  });
  const parsedCells = useMemo(
    () => [
      { label: "Add New Cell", value: "add-new" },
      ...(cells
        ?.map((x) => {
          return {
            label: x.locationName,
            value: x.cellId,
            status: getCellStatus(devices, x.cellId),
          };
        })
        .sort((x, y) => x.label.localeCompare(y.label)) ?? []),
    ],
    [cells, devices]
  );
  const {
    selectedItem: [, setSelectedItem],
  } = useDrawerLayoutContext({
    sx: {
      position: { md: "sticky" },
      top: { md: 88 },
      height: { md: "calc(100dvh - 88px)" },
      "& .MuiDrawer-paper": {
        height: { md: "100%" },
        overflowY: { md: "auto" },
        overflowX: { md: "hidden" },
        overscrollBehaviorY: { md: "contain" },
      },
    },
    listItems: parsedCells,
    isLoading,
    onItemSelected: (item: { label: string; value: string }) => {
      navigate({
        to: "/admin/cell/$id",
        params: {
          id: item.value,
        },
      });
    },
  });

  useEffect(() => {
    const selected = parsedCells.find((x) => x.value === id);
    setSelectedItem(selected);
  }, [id, parsedCells, setSelectedItem]);
  return (
    <>
      <PageHeader>Manage Cells</PageHeader>
      <Outlet />
    </>
  );
}
